import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/forms/components/PasswordInput'
import { PasswordStrengthBar } from '@/forms/components/PasswordStrengthBar'
import { usePasswordStrength } from '@/hooks/usePasswordStrength'
import API from '@/lib/api'
import { AuthCard } from '@/layouts/AuthLayout'
import { useAuth } from '@/lib/auth'
import { passwordMinLength } from '@/lib/crypto/passwordMinLength'
import { cn } from '@/lib/utils'

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form'

interface SignupFormProps {
  email?: string
  onSuccess?: () => void
}

interface SignupFormValues {
  fullName: string
  email: string
  password: string
  passwordConfirm: string
}

const resolver = async (values: SignupFormValues) => {
  const errors: Record<string, { type: string; message: string }> = {}

  if (!values.fullName) {
    errors.fullName = {
      type: 'required',
      message: 'Je třeba vyplnit vaše jméno',
    }
  }

  if ( ! values.fullName.includes(' ')) {
    errors.fullName = {
      type: 'invalid',
      message: 'Je třeba zadat jméno a příjmení',
    }
  }

  if (!values.email) {
    errors.email = {
      type: 'required',
      message: 'E-mail je povinný',
    }
  }

  if (!values.password) {
    errors.password = {
      type: 'required',
      message: 'Heslo je povinné',
    }
  }

  if (values.password.length < passwordMinLength) {
    errors.password = {
      type: 'length',
      message: `Heslo musí mít alespoň ${passwordMinLength} znaků`,
    }
  }

  if (!values.passwordConfirm) {
    errors.passwordConfirm = {
      type: 'required',
      message: 'Potvrzení hesla je povinné',
    }
  }

  if (values.password !== values.passwordConfirm) {
    errors.passwordConfirm = {
      type: 'mismatch',
      message: 'Hesla se neshodují',
    }
  }

  if (values.email !== '' && values.password.indexOf(values.email) !== -1) {
    errors.password = {
      type: 'email',
      message: 'Heslo nesmí obsahovat e-mail',
    }
  }

  return {
    values,
    errors,
  }
}

export function SignupForm({
  email,
  onSuccess,
  className,
  ...props
}: React.ComponentProps<'div'> & SignupFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const methods = useForm<SignupFormValues>({
    resolver: resolver as unknown as Resolver<SignupFormValues>,
    defaultValues: {
      fullName: '',
      email: email || '',
      password: '',
      passwordConfirm: '',
    },
  })
  const { csrf } = useAuth({
    middleware: 'guest',
    redirectTo: '/',
    tmp: 'SignupForm',
  })

  const password = methods.watch('password')
  const passwordStrengthResult = usePasswordStrength(password || '')

  const onSubmit = async (values: SignupFormValues) => {
    if (isSubmitting) return
    if (Object.keys(methods.formState.errors).length > 0) return
    if (!values.email || !values.password) return

    setIsSubmitting(true)

    await csrf()

    API.post(
      '/api/v2/auth/signup',
      {
        fullName: values.fullName,
        email: values.email,
        password: values.password,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
      .then((response) => {

        setIsSubmitting(false)
        if (response.status >= 200 && response.status < 300) {
          // User created and has been assigned ID
          return onSuccess && onSuccess()
        } else {
          toast.error('Registrace neproběhla', {
            description: 'Registraci se nepodařilo zpracovat, zkuste to prosím znovu později.',
          })
        }
      })
      .catch((error) => {
        setIsSubmitting(false)
        console.error(error)
        toast.error('Registrace neproběhla', {
          description: error?.response?.data?.type === 'user-already-exists'
            ? 'Účet s tímto e-mailem už existuje. Zkuste se přihlásit.'
            : 'Registraci se nepodařilo zpracovat, zkuste to prosím znovu později.',
        })
        return error
      })
  }

  return (
    <div className={cn('flex w-full flex-col gap-4', className)} {...props}>
      <AuthCard title="Vytvořit účet" description="Fakturace, náklady a banka na jednom místě.">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className="grid gap-5" noValidate>
              <FormField
                control={methods.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem className="gap-1.5">
                    <FormLabel className="text-[13px]">Jméno a příjmení</FormLabel>
                    <FormControl>
                      <Input disabled={isSubmitting} autoComplete="name" autoFocus {...field} />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={methods.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="gap-1.5">
                    <FormLabel className="text-[13px]">E-mail</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="jmeno@firma.cz" disabled={isSubmitting} autoComplete="email" {...field} />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={methods.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="gap-1.5">
                    <FormLabel className="text-[13px]">Heslo</FormLabel>
                    <FormControl>
                      <PasswordInput
                        disabled={isSubmitting}
                        autoComplete="new-password"
                        minLength={passwordMinLength}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                    <PasswordStrengthBar score={passwordStrengthResult?.score} />
                    <p className="text-xs text-muted-foreground">Alespoň {passwordMinLength} znaků.</p>
                  </FormItem>
                )}
              />
              <FormField
                control={methods.control}
                name="passwordConfirm"
                render={({ field }) => (
                  <FormItem className="gap-1.5">
                    <FormLabel className="text-[13px]">Heslo znovu</FormLabel>
                    <FormControl>
                      <PasswordInput disabled={isSubmitting} autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

            <Button className="mt-1 w-full" disabled={isSubmitting} type="submit">
              {isSubmitting && <Loader2 className="animate-spin" />}
              {isSubmitting ? 'Vytváříme účet' : 'Vytvořit účet'}
            </Button>
          </form>
        </FormProvider>
      </AuthCard>

      <p className="text-center text-sm text-muted-foreground">
        Máte už účet?{' '}
        <Link to="/auth/login" className="font-medium text-foreground underline-offset-4 hover:underline">
          Přihlaste se
        </Link>
      </p>
    </div>
  )
}
